import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';

import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';

import { AllowedIpFilterOptions, AllowedIpOrderingOptions } from '../models/allowed-ip-filter-option';

import * as AllowedIpRegisterActions from './allowed-ip-register.actions';
import { allowedIpRegisterAdapterActions } from './allowed-ip-register.actions';

import {
   AllowedIpRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './allowed-ip-register.reducer';

import { AllowedIpRegisterListItem, AllowedIpRegisterResults } from '../models/allowed-ip-register-results.model';

import {
   GetAllowedIpRegister,
   GetAllowedIpRegister_allowedIpAddressesConnection_items,
} from '../queries/generated/GetAllowedIpRegister';

import { EditAllowedIpComponent } from '../../edit-allowed-ip/containers/edit-allowed-ip.component';
import { AllowedIpRegisterOptions } from '../models/allowed-ip-register-options';
import { AllowedIpRegisterApiService } from '../services/allowed-ip-register-api.service';
import * as AllowedIpRegisterSelectors from './allowed-ip-register.selectors';
import { selectFilters } from './allowed-ip-register.selectors';

@Injectable()
export class AllowedIpRegisterEffects {
   dialogId: string;

   constructor(
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AllowedIpRegisterState>,
      private allowedIpRegisterApiService: AllowedIpRegisterApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private dialogService: LcsDialogService
   ) {}

   loadAllowedIpList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            allowedIpRegisterAdapterActions.pageEnter,
            AllowedIpRegisterActions.refreshList,
            AllowedIpRegisterActions.editAllowedIpSuccess
         ),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, quickFilters, orderingOptions]) =>
            this.loadNewData(filters, quickFilters, orderingOptions)
         )
      );
   });

   loadAllowedIpPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(allowedIpRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(AllowedIpRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, endCursor, quickFilters, orderingOptions]) =>
            this.loadNewPage(endCursor, filters, quickFilters, orderingOptions)
         )
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(allowedIpRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(AllowedIpRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, quickFilters, filters, orderingOptions]) =>
            this.loadNewData(filters, quickFilters, orderingOptions)
         )
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(allowedIpRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, quickFilters, orderingOptions]) =>
            this.loadNewData(filters, quickFilters, orderingOptions)
         )
      );
   });

   deleteAllowedIp$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AllowedIpRegisterActions.deleteAllowedIp),
         switchMap((action) => {
            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Remove Allowed IP?',
                  message: 'Are you sure you would like to remove this allowed IP?',
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.allowedIpRegisterApiService.deleteAllowedIp(action.allowedIpAddressId).pipe(
                        map(() =>
                           AllowedIpRegisterActions.deleteAllowedIpSuccess({
                              ipAddress: action.ipAddress,
                           })
                        ),
                        catchError((error) => of(AllowedIpRegisterActions.deleteAllowedIpFailure(error)))
                     );
                  }

                  return EMPTY;
               })
            );
         })
      );
   });

   deleteAllowedIpSuccess$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AllowedIpRegisterActions.deleteAllowedIpSuccess),
         switchMap((action) => {
            this.dialogService.closeFormComponent();
            this.snackBar.open(`IP Address ${action.ipAddress} removed`);

            return of(AllowedIpRegisterActions.refreshList());
         })
      );
   });

   editAllowedIp$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AllowedIpRegisterActions.editAllowedIp),
            switchMap((action) => {
               const dialogRef = this.dialogService.openFormComponent(EditAllowedIpComponent, {
                  closeOnNavigation: true,
                  disableClose: true,
                  data: {
                     allowedIp: action.allowedIp,
                  },
                  panelClass: ['dialog-md', 'dialog-container'],
               });

               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   changeAllowedIpInformation$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AllowedIpRegisterActions.editAllowedIpSubmit),
         switchMap((action) => {
            return this.allowedIpRegisterApiService
               .changeAllowedIpData(
                  action.allowedIpAddressId,
                  action.qManageTicketId ?? '',
                  action.accountId,
                  action.fields
               )
               .pipe(
                  map(() => {
                     this.snackBar.open(`Allowed IP Edited`);
                     return AllowedIpRegisterActions.editAllowedIpSuccess();
                  }),
                  catchError((error) => {
                     return of(AllowedIpRegisterActions.editAllowedIpFailure(error));
                  })
               );
         })
      );
   });

   private loadNewData(
      filters: AllowedIpFilterOptions[],
      quickFilters: AllowedIpRegisterOptions | null = null,
      orderingOptions: AllowedIpOrderingOptions[] | null = null
   ) {
      return this.allowedIpRegisterApiService.getAllowedIps(null, filters, quickFilters, orderingOptions).pipe(
         map((result) =>
            AllowedIpRegisterActions.allowedIpRegisterAdapterActions.loadSuccess(this.convertResult(result.data))
         ),
         catchError((error) => of(AllowedIpRegisterActions.allowedIpRegisterAdapterActions.loadFailure(error)))
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: AllowedIpFilterOptions[],
      quickFilters: AllowedIpRegisterOptions | null = null,
      orderingOptions: AllowedIpOrderingOptions[] | null = null
   ) {
      return this.allowedIpRegisterApiService.getAllowedIps(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) =>
            AllowedIpRegisterActions.allowedIpRegisterAdapterActions.loadPageSuccess(this.convertResult(result.data))
         ),
         catchError((error) => of(AllowedIpRegisterActions.allowedIpRegisterAdapterActions.loadPageFailure(error)))
      );
   }

   private convertResult(getAllowedIp: GetAllowedIpRegister): AllowedIpRegisterResults {
      return {
         endCursor: getAllowedIp.allowedIpAddressesConnection.pageInfo.endCursor,
         hasNextPage: getAllowedIp.allowedIpAddressesConnection.pageInfo.hasNextPage,
         items: getAllowedIp.allowedIpAddressesConnection.items.map(this.convertListItem),
         totalCount: getAllowedIp.allowedIpAddressesConnection.totalCount,
      };
   }

   private convertListItem(item: GetAllowedIpRegister_allowedIpAddressesConnection_items): AllowedIpRegisterListItem {
      return {
         allowedIpAddressId: item?.allowedIpAddressId ?? null,
         ipAddress: item?.ipAddress ?? null,

         accountId: item?.account?.accountId ?? item?.accountId ?? null,
         accountName: item?.account?.name ?? null,
         qManageTicketId: item?.qManageTicketId ?? null,
         originatingType: item?.originatingType ?? null,
         status: item?.status ?? null,
         requestedByUserId: item?.requestedByUser?.userId ?? item?.requestedByUserId ?? null,
         requestedByUserName: item?.requestedByUser?.name ?? null,
         createDate: item?.createDate ?? null,
         updateDate: item?.updateDate ?? null,
      };
   }
}
